import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import type { ExistingImageData, ImageInsertData } from './imageInsertTypes';
import { ModalFooter } from './ModalFooter';

interface ExistingImagePreviewProps {
  image: ExistingImageData;
  onReplace: () => void;
  onEditWithAI: () => void;
  onRemoveImage: () => void;
  onInsert: (data: ImageInsertData) => void;
  onClose: () => void;
}

export function ExistingImagePreview({
  image,
  onReplace,
  onEditWithAI,
  onRemoveImage,
  onInsert,
  onClose,
}: ExistingImagePreviewProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const [altText, setAltText] = useState(image.alt || '');
  const [label, setLabel] = useState('');
  const initialAlt = image.alt || '';
  const hasChanges = altText.trim() !== initialAlt.trim();

  const handleAltTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAltText(e.target.value);
  }, []);

  const handleLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.stopPropagation();
    }
  }, []);

  const handleSave = useCallback(() => {
    onInsert({
      src: image.src,
      alt: altText.trim(),
      label: label.trim() || undefined,
    });
    onClose();
  }, [image.src, altText, label, onInsert, onClose]);

  return (
    <>
      <div style={styles.content}>
        <div style={styles.previewContainer}>
          <img
            src={image.src}
            alt={altText || 'Current image'}
            style={styles.previewImage}
          />
        </div>

        <div style={styles.imageActions}>
          <button
            type="button"
            style={styles.pillButton}
            onClick={onReplace}
            aria-label="Replace this image"
          >
            Replace Image
          </button>
          <button
            type="button"
            style={styles.pillButtonPrimary}
            onClick={onEditWithAI}
            aria-label="Edit this image with AI"
          >
            Edit with AI
          </button>
          <div style={styles.spacer} />
          <button
            type="button"
            style={styles.removeButton}
            onClick={onRemoveImage}
            aria-label="Remove this image"
          >
            Remove Image
          </button>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="existing-alt-text">
            <span style={styles.labelRow}>
              <span>Alt Text</span>
              {image.aiGeneratedAlt && (
                <span style={styles.aiBadge} aria-label="AI Generated">
                  AI Generated
                </span>
              )}
            </span>
          </label>
          <textarea
            id="existing-alt-text"
            value={altText}
            onChange={handleAltTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Describe the image for accessibility"
            style={styles.textarea}
            rows={3}
          />
          <p style={styles.hint}>
            Required for accessibility. Describe what's shown, not what it means.
          </p>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="existing-label">
            Label (optional)
          </label>
          <input
            id="existing-label"
            type="text"
            value={label}
            onChange={handleLabelChange}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Team meeting photo"
            style={styles.input}
            disabled
            aria-describedby="existing-label-hint"
          />
          <p id="existing-label-hint" style={styles.hint}>
            Not shown to learners — used to search and organize your image library
          </p>
        </div>
      </div>

      <ModalFooter
        onCancel={onClose}
        actions={[
          {
            label: 'Save Changes',
            onClick: handleSave,
            disabled: !hasChanges || !altText.trim(),
            ariaLabel: 'Save changes to image',
            variant: 'primary',
          },
        ]}
      />
    </>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    content: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      flex: 1,
      overflow: 'auto',
    },
    previewContainer: {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: tokens.backgrounds.app,
      borderRadius: '8px',
      border: `1px solid ${tokens.text.subtle}`,
      padding: '16px',
    },
    previewImage: {
      maxWidth: '100%',
      maxHeight: '240px',
      objectFit: 'contain',
      borderRadius: '4px',
    },
    imageActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    pillButton: {
      padding: '8px 16px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '20px',
      background: 'none',
      cursor: 'pointer',
      color: tokens.text.primary,
      whiteSpace: 'nowrap',
    },
    pillButtonPrimary: {
      padding: '8px 16px',
      fontSize: '13px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '20px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    spacer: {
      flex: 1,
    },
    removeButton: {
      padding: '8px 16px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${tokens.ui.errorBorder}`,
      borderRadius: '20px',
      background: 'none',
      cursor: 'pointer',
      color: tokens.ui.errorText,
      whiteSpace: 'nowrap',
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    label: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    labelRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    aiBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 600,
      borderRadius: '10px',
      backgroundColor: `color-mix(in srgb, ${tokens.ui.selectionBorder} 15%, transparent)`,
      color: tokens.ui.selectionBorder,
      textTransform: 'none' as const,
      letterSpacing: '0',
    },
    textarea: {
      padding: '10px 12px',
      fontSize: '14px',
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      outline: 'none',
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '60px',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    input: {
      padding: '10px 12px',
      fontSize: '14px',
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      outline: 'none',
      fontFamily: 'inherit',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    hint: {
      fontSize: '12px',
      color: tokens.text.subtle,
      margin: 0,
    },
  };
}
